import { Formik } from 'formik';
import update from 'immutability-helper';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { Component } from 'react';

import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';
import { Loader } from '@hivebrite/design-system/Loader';

import { withTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import { displayXhrError } from 'hb-react/frontoffice/utils/globalUtils';
import ModalWithTemplate from 'hb-react/shared/components/DesignSystem/Modal/ModalWithTemplate';
import {
  CustomAttribute,
  Textarea,
} from 'hb-react/shared/components/formik/components/inputs';
import RequiredExplanation from 'hb-react/shared/components/formik/components/RequiredExplanation';
import { customAttributesInitialValues } from 'hb-react/shared/components/formik/utils/customAttributes';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';
import dayjs from 'hb-react/shared/utils/dayjs/dayjs';

const MessageInput = () => (
  <Textarea
    ariaLabel={s__('Accessibility|Message for group approval')}
    minHeight={90}
    name="approval[message]"
  />
);

const getHasAnyRequiredCustomAttrs = (customAttributes) =>
  customAttributes
    ? customAttributes.length > 0 &&
      customAttributes.some((customAttribute) => customAttribute.required)
    : false;

const getShouldShowSuccessDirectly = (
  status,
  createdAt,
  autoApprovalListId,
  hasAnyRequiredCustomAttrs,
) =>
  status === 'approved' &&
  ((!!createdAt && !!autoApprovalListId) || !hasAnyRequiredCustomAttrs);

class MemberRequest extends Component {
  _isMounted = true;

  state = {
    editMode: false,
    initialValues: {
      approval: {},
    },
    isSaving: false,
  };

  componentDidMount() {
    this.initForm();
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  safeSetState = (state) => {
    if (this._isMounted) {
      this.setState(state);
    }
  };

  // HANDLERS

  handleSubmit = (values, actions) => {
    const { topicId, onUpdate, setDisplayRequestModal } = this.props;
    const {
      initialValues: { approval },
    } = this.state;
    const approvalId = approval.id || this.props.approvalId;

    this.setState({ isSaving: true });

    if (approvalId) {
      axios
        .patch(Routes.topics_topic_approval_path(topicId, approvalId), values)
        .then((response) => {
          actions.setSubmitting(false);
          this.updateApproval(response.data.approval);
          onUpdate(response.data.approval);
          setDisplayRequestModal(false);
        })
        .catch((error) => displayXhrError(error.message))
        .finally(() => this.safeSetState({ isSaving: false }));
    } else {
      axios
        .post(Routes.topics_topic_approvals_path(topicId), values)
        .then((response) => {
          actions.setSubmitting(false);
          if (!response.data.approval.following) {
            this.updateApproval(response.data.approval);
          }
          onUpdate(response.data.approval);
          setDisplayRequestModal(false);
        })
        .catch((error) => displayXhrError(error.message))
        .finally(() => this.safeSetState({ isSaving: false }));
    }

    return false;
  };

  handleEditMode = () => {
    this.setState((previousState) =>
      update(previousState, { editMode: { $set: !previousState.editMode } }),
    );
  };

  handleDelete = (displaySuccessNotification = true) => {
    const { topicId, onCancel } = this.props;
    const {
      initialValues: {
        approval: { id },
      },
    } = this.state;

    this.setState({ isSaving: true });

    if (id) {
      return axios
        .delete(Routes.topics_topic_approval_path(topicId, id))
        .then(() => {
          onCancel(displaySuccessNotification);
        })
        .catch((error) => {
          displayXhrError(error.message);
          this.safeSetState({ isSaving: false });
        });
    }

    return onCancel(false);
  };

  // LOGIC

  verifyIfWeShouldFillMessageToJoin = () => {
    const { editMode } = this.state;
    const { approvalId } = this.props;

    if (editMode) {
      return true;
    }

    return !approvalId;
  };

  updateApproval = (approval = {}) =>
    this.setState((previousState) =>
      update(previousState, {
        initialValues: { approval: { $set: approval } },
      }),
    );

  initForm = () => {
    const { topicId, approvalId } = this.props;
    let approvalPath;

    if (approvalId) {
      approvalPath = Routes.topics_topic_approval_path(topicId, approvalId);
    } else {
      approvalPath = Routes.new_topics_topic_approval_path(topicId);
    }

    axios
      .get(approvalPath)
      .then((response) => this.updateApproval(response.data.approval))
      .catch((error) => displayXhrError(error.message))
      .finally(() => this.safeSetState({ isSaving: false }));
  };

  renderHeader = () => {
    const {
      initialValues: {
        approval: { created_at: createdAt },
      },
    } = this.state;
    const { isPublicAndRequiresUserToFillCustomAttributesToJoin } = this.props;

    if (isPublicAndRequiresUserToFillCustomAttributesToJoin) {
      return (
        <Box pb={4} font="h4-regular">
          {s__('Topics|Please provide the following information to join')}
        </Box>
      );
    }

    return (
      <Box pb={4} display="block">
        <Box font="h4-regular" mb={2}>
          {I18n.t('web.topics.member_requests.restricted_access_msg_1')}
        </Box>
        {createdAt ? (
          <Box display="block">
            {I18n.t('web.topics.member_requests.pending_request')}
            <Box font="metadata-regular-asphalt" mt={2}>
              {I18n.t('web.topics.member_requests.requested_at', {
                date: dayjs(createdAt).format('LLL'),
              })}
            </Box>
          </Box>
        ) : (
          I18n.t('web.topics.member_requests.restricted_access_msg_2')
        )}
      </Box>
    );
  };

  renderEditView = () => {
    const { isSaving } = this.state;

    return (
      <Box justifyContent="flex-end" mt={8} data-testid="edit-view">
        <Button
          variant="secondary"
          isDisabled={isSaving}
          onClick={this.handleDelete}
        >
          {I18n.t('web.topics.member_requests.cancel')}
        </Button>
        <Box ml={3}>
          <Button isDisabled={isSaving} onClick={this.handleEditMode}>
            {I18n.t('web.topics.member_requests.edit')}
          </Button>
        </Box>
      </Box>
    );
  };

  render() {
    const {
      initialValues: { approval },
      isSaving,
    } = this.state;
    const {
      custom_attributes: customAttributes,
      created_at: createdAt,
      message,
      id,
      auto_approval_user_list_id: autoApprovalListId,
      status,
    } = approval;

    const shouldFillMessageToJoin = this.verifyIfWeShouldFillMessageToJoin();

    const hasAnyRequiredCustomAttrs =
      getHasAnyRequiredCustomAttrs(customAttributes);

    const shouldShowSuccessDirectly = getShouldShowSuccessDirectly(
      status,
      createdAt,
      autoApprovalListId,
      hasAnyRequiredCustomAttrs,
    );

    if (shouldShowSuccessDirectly) {
      // an other way to refresh the topic page could be cool
      window.location.reload(false);
    }

    return (
      <ModalWithTemplate
        onCloseModal={() => {
          const { approvalId, setDisplayRequestModal } = this.props;

          // When there is a new request to join
          if (!approvalId) {
            // We delete the created approval and
            // do not display the success notification message
            this.handleDelete(false);
          } else {
            setDisplayRequestModal(false);
          }
        }}
        header={s__('Topics|Request to join')}
        screenReadersCloseButtonText={s__(
          'TopicsAccessibility|Close request to join window',
        )}
      >
        <Box display="block" width="100%">
          {isEmpty(customAttributes) && !('message' in approval) ? (
            <Loader size={50} />
          ) : (
            <>
              {!shouldShowSuccessDirectly && this.renderHeader()}
              {!shouldShowSuccessDirectly && shouldFillMessageToJoin && (
                <Formik
                  initialValues={{
                    approval: {
                      ...customAttributesInitialValues(customAttributes),
                      message,
                    },
                  }}
                  onSubmit={this.handleSubmit}
                >
                  {(formik) => (
                    <form
                      // eslint-disable-next-line
                      ref={(ref) => (this.form = ref)}
                      data-testid="form"
                      method="post"
                      onReset={formik.handleReset}
                      onSubmit={formik.handleSubmit}
                    >
                      {'message' in approval ? (
                        <MessageInput />
                      ) : (
                        <>
                          <RequiredExplanation />
                          {customAttributes.map((customAttribute) => {
                            const { id, name } = customAttribute;
                            const inputName = `approval[${name}]`;

                            return (
                              <CustomAttribute
                                key={id}
                                name={inputName}
                                customAttribute={customAttribute}
                              />
                            );
                          })}
                        </>
                      )}
                      <Box justifyContent="flex-end" mt={8}>
                        <Button type="submit" isLoading={isSaving}>
                          {I18n.t('actions.send')}
                        </Button>
                      </Box>
                    </form>
                  )}
                </Formik>
              )}

              {!shouldShowSuccessDirectly &&
                id &&
                !shouldFillMessageToJoin &&
                this.renderEditView()}

              {/* Display the ok message while it reload */}
              {shouldShowSuccessDirectly && (
                <Box>{s__('Topics|Thank you for joining this group!')}</Box>
              )}
            </>
          )}
        </Box>
      </ModalWithTemplate>
    );
  }
}

MemberRequest.propTypes = {
  approvalId: PropTypes.number,
  isPublicAndRequiresUserToFillCustomAttributesToJoin: PropTypes.bool,
  onUpdate: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default withTopicContext(MemberRequest);
